import React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { configureAutoTrack } from "aws-amplify/analytics";
import { MantineProvider } from "@mantine/core";
import { Authenticator } from "@aws-amplify/ui-react";
import ProductLayout from "./components/Product/Layout/Layout";
import productRoutes from "./routes/productRoutes";
import productEligibilityRoutes from "./routes/productEligibilityRoutes";
import accountRoutes from "./routes/accountRoutes";
import routesWithoutSignup from "./routes/routesWithoutSignup";
import { ProductContextProvider } from "./contexts/ProductContext";
import { EligibilityProvider } from "./contexts/EligibilityContext";
import { PatientProvider } from "./contexts/PatientContext";
import ClaimableAuthenticator from "./components/Shared/ClaimableAuthenticator";
import Page from "./components/Product/Layout/Page";
import theme from "./theme";
import IdentifyUser from "./components/IdentifyUser";
import PathConstants from "./routes/pathConstants";

import "@aws-amplify/ui-react/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
      allowGuestAccess: true,
    },
  },
  Analytics: {
    Pinpoint: {
      appId: import.meta.env.VITE_PINPOINT_APP_ID,
      region: "us-east-1",
    },
  },
});

configureAutoTrack({
  enable: true,
  type: "session",
});

configureAutoTrack({
  enable: true,
  type: "pageView",
  options: {
    appType: "singlePage",
  },
});

const ResumeAppeal = React.lazy(
  () => import("./pages/Product/Resume/ResumeAppeal"),
);

function App() {
  const router = createBrowserRouter([
    {
      element: (
        <ClaimableAuthenticator>
          <ProductContextProvider>
            <ProductLayout />
          </ProductContextProvider>
        </ClaimableAuthenticator>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: productRoutes,
    },
    {
      element: (
        <ProductContextProvider>
          <EligibilityProvider>
            <PatientProvider>
              <ProductLayout />
            </PatientProvider>
          </EligibilityProvider>
        </ProductContextProvider>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: productEligibilityRoutes,
    },
    {
      element: (
        <ClaimableAuthenticator defaultTab="signIn">
          <ProductContextProvider>
            <ProductLayout />
          </ProductContextProvider>
        </ClaimableAuthenticator>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: accountRoutes,
    },
    {
      element: (
        <ClaimableAuthenticator defaultTab="signIn" hideSignUp>
          <ProductContextProvider>
            <ProductLayout />
          </ProductContextProvider>
        </ClaimableAuthenticator>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: routesWithoutSignup,
    },
    {
      element: (
        <ProductContextProvider>
          <ProductLayout />
        </ProductContextProvider>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: [
        { path: PathConstants.ResumeAppeal, element: <ResumeAppeal /> },
      ],
    },
  ]);

  return (
    <MantineProvider theme={theme}>
      <Authenticator.Provider>
        <RouterProvider router={router} />
        <IdentifyUser />
      </Authenticator.Provider>
    </MantineProvider>
  );
}

export default App;
